import type { FC } from 'react';

import THEME from 'constants/theme';

import { AppLoader, Error, useZoomDisable } from '@cuemath/leap';
import isPropValid from '@emotion/is-prop-valid';
import CourseSwitcherProvider from 'context/course-context';
import useSentryCaptureException from 'hooks/use-sentry-capture-exception';
import useTrackComponentMounted from 'hooks/use-track-component-mounted';
import React, { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { ALL_ROUTES } from 'router/config';
import {
  StyleSheetManager,
  type ShouldForwardProp,
  ThemeProvider,
} from 'styled-components';

import 'utils/intersection-observer';

import { getStore } from './store';

const { store, persistor } = getStore();
const router = createBrowserRouter(ALL_ROUTES);

const shouldForwardProp: ShouldForwardProp<'web'> = (propName, elementToBeRendered) =>
  typeof elementToBeRendered === 'string' ? isPropValid(propName) : true;

const App: FC = () => {
  const sentryCaptureException = useSentryCaptureException();

  useEffect(() => {
    document.getElementById('app-loader-wrapper')?.remove();
  }, []);

  useZoomDisable();
  useTrackComponentMounted('App');

  return (
    <StyleSheetManager enableVendorPrefixes shouldForwardProp={shouldForwardProp}>
      <ThemeProvider theme={THEME}>
        <ErrorBoundary
          fallback={<Error height="100vh" />}
          onError={sentryCaptureException}
        >
          <Suspense fallback={<AppLoader height="100vh" />}>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <CourseSwitcherProvider>
                  <RouterProvider router={router} />
                </CourseSwitcherProvider>
              </PersistGate>
            </Provider>
          </Suspense>
        </ErrorBoundary>
      </ThemeProvider>
    </StyleSheetManager>
  );
};

export default App;
